import { useState, useMemo, useCallback } from 'react';
import { Dialog } from '@components/dialog';
import { Button } from '@components/button';
import { IconButton } from '@components/iconButton';
import { Box, Typography, Alert } from '@mui/material';
import { Autocomplete } from '@components/autocomplete';
import { TextField } from '@components/textField';
import { useStructuredQuote } from '@services/quoteDetails';
import { getEngine } from '@lio/rule-engine';
import { QuoteFieldControl } from '@components/quoteDetails/QuoteFieldControl';
import { UIFieldType } from '@services/data/productModelTypes';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { shallow } from 'zustand/shallow';
import { TrashAlt, PenSolid, ChevronUp } from '@assets/svgs';
import { useUpdateQuoteItemAttributes } from '@services/quoteItem';
import { enqueueSnackbar } from 'notistack';
import { PanelInstance } from '@store/Quote/Quote.types';
import cuid from 'cuid';

interface ModifyAttributeModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string;
}

type PanelAttributeType = PanelInstance['quoteItem']['attributeTypes'][string];

interface AttributeOption {
  label: string;
  value: string;
  quoteItemTypeCodeName: string;
  instanceNumber?: number;
  attributeType: {
    label: string | null;
    helpText: string | null;
    deprecated: boolean | null;
  } & Omit<PanelAttributeType, 'label' | 'helpText'>;
  quoteItemId: string;
}

interface AttributeEntry {
  id: string;
  selectedAttribute: AttributeOption | null;
  value: any;
  error: string;
}

export const ModifyAttributeModal = ({ isOpen, onClose, quoteId }: ModifyAttributeModalProps) => {
  const [entries, setEntries] = useState<AttributeEntry[]>([
    {
      id: `temp-${cuid()}`,
      selectedAttribute: null,
      value: null,
      error: '',
    },
  ]);
  const [activeEntryIndex, setActiveEntryIndex] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  const { structuredQuote } = useStructuredQuote(quoteId);
  const setAttributeValue = useQuoteStore((state) => state.setAttributeValue, shallow);
  const clearAttributeValues = useQuoteStore((state) => state.clearAttributeValues, shallow);
  const updateAttributesMutation = useUpdateQuoteItemAttributes();

  // Get all available attribute codenames as options
  const attributeOptions = useMemo<AttributeOption[]>(() => {
    if (!structuredQuote) return [];

    const options: AttributeOption[] = [];

    structuredQuote.quoteItems.forEach((quoteItem) => {
      Object.values(quoteItem.attributeTypes).forEach((attributeType) => {
        const displayName = attributeType.codeName;
        const quoteItemName = quoteItem.quoteItemType.codeName;
        const instance = quoteItem.instanceNumber ? ` (Instance ${quoteItem.instanceNumber})` : '';

        options.push({
          label: `${displayName} - ${quoteItemName}${instance}`,
          value: attributeType.codeName,
          quoteItemTypeCodeName: quoteItem.quoteItemType.codeName,
          instanceNumber: quoteItem.instanceNumber ?? undefined,
          attributeType: {
            ...attributeType,
            label: typeof attributeType.label === 'string' ? attributeType.label : null,
            helpText: typeof attributeType.helpText === 'string' ? attributeType.helpText : null,
            deprecated: typeof attributeType.deprecated === 'boolean' ? attributeType.deprecated : null,
          },
          quoteItemId: quoteItem.id,
        });
      });
    });

    // Sort by codename for better UX
    return options.sort((a, b) => a.value.localeCompare(b.value));
  }, [structuredQuote]);

  // Add new entry
  const addEntry = () => {
    const newEntry: AttributeEntry = {
      id: `temp-${cuid()}`,
      selectedAttribute: null,
      value: null,
      error: '',
    };
    setEntries((prev) => {
      const newEntries = [...prev, newEntry];
      setActiveEntryIndex(newEntries.length - 1); // Set new entry as active
      return newEntries;
    });
  };

  // Set entry as active for editing
  const setActiveEntry = (entryIndex: number) => {
    setActiveEntryIndex(entryIndex);
  };

  // Check if an entry is valid (can be collapsed)
  const isEntryValid = (entry: AttributeEntry) => {
    // FUTURE: may not want to actually force a value, intent may be to delete but leaving for now
    const hasValue =
      entry.value !== null &&
      entry.value !== undefined &&
      (typeof entry.value !== 'string' || entry.value.trim() !== '');

    return Boolean(entry.selectedAttribute) && hasValue && !entry.error;
  };

  // Collapse current entry - find next valid entry to activate or default to last entry
  const collapseActiveEntry = () => {
    // Find the last entry index
    const lastEntryIndex = entries.length - 1;
    setActiveEntryIndex(lastEntryIndex);
  };

  // Remove entry
  const removeEntry = (entryIndex: number) => {
    setEntries((prev) => {
      const newEntries = prev.filter((_, index) => index !== entryIndex);
      // Adjust activeEntryIndex if needed
      if (entryIndex === activeEntryIndex) {
        // If removing active entry, set last entry as active
        setActiveEntryIndex(Math.max(0, newEntries.length - 1));
      } else if (entryIndex < activeEntryIndex) {
        // If removing entry before active, adjust index
        setActiveEntryIndex(activeEntryIndex - 1);
      }
      return newEntries;
    });
  };

  // Update entry value
  const updateEntryValue = (entryIndex: number, newValue: any) => {
    setEntries((prev) => prev.map((entry, index) => (index === entryIndex ? { ...entry, value: newValue } : entry)));
  };

  // Handle attribute selection for an entry
  const handleAttributeSelection = (entryIndex: number, option: AttributeOption | null) => {
    setEntries((prev) =>
      prev.map((entry, index) => {
        if (index === entryIndex) {
          if (!option) {
            return { ...entry, id: `temp-${cuid()}`, selectedAttribute: null, value: null, error: '' };
          }

          // Set initial value based on existing attribute value
          const currentValue = option.attributeType.attribute?.value;
          const newId = `${option.quoteItemId}-${option.attributeType.id}`;
          return {
            ...entry,
            id: newId,
            selectedAttribute: option,
            value: currentValue ?? undefined,
            error: '',
          };
        }
        return entry;
      }),
    );
  };

  // Check if all entries are valid and determine which entries can show add button
  const allEntriesValid = entries.every((entry) => {
    const hasValue =
      entry.value !== null &&
      entry.value !== undefined &&
      (typeof entry.value !== 'string' || entry.value.trim() !== '');
    return entry.selectedAttribute && hasValue && !entry.error;
  });

  // Save the modified attributes
  const handleSave = useCallback(async () => {
    if (!allEntriesValid) return;

    setIsSaving(true);
    try {
      // Prepare the request array for the backend
      const quoteItemsMap: Record<string, any[]> = {};

      // Group attributes by quote item
      for (const entry of entries) {
        if (!entry.selectedAttribute) continue;

        const hasValue =
          entry.value !== null &&
          entry.value !== undefined &&
          (typeof entry.value !== 'string' || entry.value.trim() !== '');

        if (!hasValue) continue;

        const quoteItemId = entry.selectedAttribute.quoteItemId;
        const attributeValueId =
          entry.selectedAttribute.attributeType.attribute?.id ??
          `${quoteItemId}-${entry.selectedAttribute.attributeType.id}`;

        if (!quoteItemsMap[quoteItemId]) {
          quoteItemsMap[quoteItemId] = [];
        }

        // Update the attribute in the store first (for immediate UI feedback)
        setAttributeValue(attributeValueId, {
          isLocked: entry.selectedAttribute.attributeType.attribute?.isLocked ?? null,
          value: entry.value,
          valueOverride: null,
          isUpdatedByUser: true,
        });

        quoteItemsMap[quoteItemId].push({
          value: entry.value,
          valueOverride: null,
          id: entry.selectedAttribute.attributeType.attribute?.id,
          quoteItemAttributeTypeId: entry.selectedAttribute.attributeType.id,
          isUpdatedByUser: true,
        });
      }

      // If we have attributes to save, save them to the backend
      if (Object.keys(quoteItemsMap).length > 0) {
        const requestArray = Object.entries(quoteItemsMap).map(([quoteItemId, quoteItemAttributes]) => ({
          id: quoteItemId,
          quoteItemAttributes,
        }));

        await updateAttributesMutation.mutateAsync({
          quoteId,
          quoteItems: requestArray,
        });

        // Clear the stored values from the store after successful save
        const savedAttributeIds = entries
          .filter((entry) => entry.selectedAttribute)
          .map((entry) => {
            return (
              entry.selectedAttribute!.attributeType.attribute?.id ??
              `${entry.selectedAttribute!.quoteItemId}-${entry.selectedAttribute!.attributeType.id}`
            );
          });

        clearAttributeValues(savedAttributeIds);

        enqueueSnackbar('Attributes saved successfully', { variant: 'success' });
      }

      onClose();
      setEntries([{ id: `temp-${cuid()}`, selectedAttribute: null, value: null, error: '' }]);
    } catch (err) {
      setEntries((prev) =>
        prev.map((entry) =>
          entry.selectedAttribute
            ? {
                ...entry,
                error: 'Failed to save attributes. Please try again.',
              }
            : entry,
        ),
      );
      console.error('Error saving attributes:', err);
      enqueueSnackbar('Failed to save attributes. Please try again.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [entries, allEntriesValid, setAttributeValue, clearAttributeValues, updateAttributesMutation, quoteId, onClose]);

  const handleClose = () => {
    setEntries([{ id: `temp-${cuid()}`, selectedAttribute: null, value: null, error: '' }]);
    setActiveEntryIndex(0);
    onClose();
  };

  // Memoize rule engine instances to prevent recreation on every render
  const ruleEngines = useMemo(() => {
    if (!structuredQuote) return new Map<string, ReturnType<typeof getEngine>>();

    const engines = new Map<string, ReturnType<typeof getEngine>>();

    entries.forEach((entry) => {
      if (entry.selectedAttribute) {
        const key = entry.selectedAttribute.quoteItemId;
        if (!engines.has(key)) {
          const engine = getEngine(structuredQuote);
          engine.setEngineInitialQuoteItem(key);
          engines.set(key, engine);
        }
      }
    });

    return engines;
  }, [structuredQuote, entries]);

  return (
    <Dialog
      id='modify-attribute-modal'
      handleCloseDialog={handleClose}
      open={isOpen}
      size='medium'
      title='Modify Attributes'
      primaryBtnText={'Save All'}
      primaryBtnAction={handleSave}
      isPrimaryBtnDisabled={!allEntriesValid || isSaving || updateAttributesMutation.isPending}
      isPrimaryBtnLoading={isSaving || updateAttributesMutation.isPending}
      secondaryBtnText='Cancel'
      secondaryBtnAction={handleClose}
      sxProps={{
        paperSx: {
          maxHeight: '90vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          mx: 0,
          mt: 0,
        }}
      >
        {entries.map((entry, index) => {
          const attributeRuleEngine =
            entry.selectedAttribute && structuredQuote
              ? (ruleEngines.get(entry.selectedAttribute.quoteItemId) ?? null)
              : null;

          const isActive = index === activeEntryIndex;

          return (
            <Box key={entry.id} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2 }}>
              {/* Entry Header with Edit and Delete Buttons */}
              {entries.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant='subtitle2'>Attribute {index + 1}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isActive && (
                      <IconButton
                        size='small'
                        onClick={() => setActiveEntry(index)}
                        sx={{ color: 'primary.main' }}
                        title='Edit this attribute'
                      >
                        <PenSolid />
                      </IconButton>
                    )}
                    {isActive && isEntryValid(entry) && index !== entries.length - 1 && (
                      <IconButton
                        size='small'
                        onClick={collapseActiveEntry}
                        sx={{ color: 'grey.600' }}
                        title='Collapse this attribute'
                      >
                        <ChevronUp />
                      </IconButton>
                    )}
                    <IconButton size='small' onClick={() => removeEntry(index)} sx={{ color: 'error.main' }}>
                      <TrashAlt />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Collapsed view for inactive entries */}
              {!isActive && entry.selectedAttribute && (
                <Box sx={{ py: 1 }}>
                  <Typography variant='body2' color='text.secondary'>
                    <strong>{entry.selectedAttribute.attributeType.codeName}</strong> -{' '}
                    {entry.selectedAttribute.quoteItemTypeCodeName}
                    {entry.selectedAttribute.instanceNumber
                      ? ` (Instance ${entry.selectedAttribute.instanceNumber})`
                      : ''}
                    :{' '}
                    <strong>
                      {entry.value !== null && entry.value !== undefined ? String(entry.value) : 'No value set'}
                    </strong>
                  </Typography>
                </Box>
              )}

              {/* Full form view for the active entry */}
              {isActive && (
                <>
                  {/* Attribute Selection */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant='body2' gutterBottom sx={{ fontWeight: 500 }}>
                      Select Attribute
                    </Typography>
                    <Autocomplete
                      options={attributeOptions}
                      value={entry.selectedAttribute}
                      onChange={(_event, newValue) =>
                        handleAttributeSelection(index, newValue as AttributeOption | null)
                      }
                      getOptionLabel={(option) => (option as AttributeOption)?.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      fullWidth
                      renderInput={(params) => <TextField {...params} placeholder='Type to search attributes...' />}
                    />
                  </Box>

                  {/* Error Display */}
                  {entry.error && (
                    <Alert severity='error' sx={{ mb: 2 }}>
                      {entry.error}
                    </Alert>
                  )}

                  {/* Attribute Info Display */}
                  {entry.selectedAttribute && (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                      <Typography variant='body2' gutterBottom sx={{ fontWeight: 500 }}>
                        Selected Attribute
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Codename:</strong> {entry.selectedAttribute.attributeType.codeName}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Label:</strong>{' '}
                        {String(attributeRuleEngine?.execute(entry.selectedAttribute.attributeType.label) || 'N/A')}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Quote Item:</strong> {entry.selectedAttribute.quoteItemTypeCodeName}
                        {entry.selectedAttribute.instanceNumber
                          ? ` (Instance ${entry.selectedAttribute.instanceNumber})`
                          : ''}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Type:</strong> {entry.selectedAttribute.attributeType.uiFieldType} (
                        {entry.selectedAttribute.attributeType.dataType})
                      </Typography>
                    </Box>
                  )}

                  {/* Value Input Field */}
                  {entry.selectedAttribute && attributeRuleEngine && (
                    <Box>
                      <Typography variant='body2' gutterBottom sx={{ fontWeight: 500 }}>
                        Value
                      </Typography>
                      <QuoteFieldControl
                        attributeType={{
                          ...entry.selectedAttribute.attributeType,
                          label: String(attributeRuleEngine.execute(entry.selectedAttribute.attributeType.label)),
                          helpText: attributeRuleEngine.execute(entry.selectedAttribute.attributeType.helpText) as
                            | string
                            | null,
                          deprecated: Boolean(
                            attributeRuleEngine.execute(entry.selectedAttribute.attributeType.deprecated),
                          ),
                          isActive: true,
                          isVisible: true,
                          isRequired: false,
                          isReadOnly: false,
                          allowOverride: false,
                        }}
                        disabledPolicyCoverages={{
                          LOIAccidentInd: false,
                          LOIInlandMarineInd: false,
                          LOIGeneralLiabilityInd: false,
                          LOIProfessionalLiabilityInd: false,
                          LOIPropertyInd: false,
                          LOISexualAbuseMolestationInd: false,
                          LOIManagementLiabilityInd: false,
                          LOIErrorsAndOmissionsInd: false,
                          LOIExcessLiabilityInd: false,
                        }}
                        errorMessage={undefined}
                        externalIcon={null}
                        hoverText={null}
                        isCollapsed={false}
                        isOverriding={false}
                        isReadOnly={false}
                        quoteId={quoteId}
                        quoteItemId={entry.selectedAttribute.quoteItemId}
                        ruleEngine={attributeRuleEngine}
                        setValue={(newValue: any) => updateEntryValue(index, newValue)}
                        uiFieldType={entry.selectedAttribute.attributeType.uiFieldType as UIFieldType}
                        value={entry.value}
                        panelInstanceId={`modify-${entry.selectedAttribute.quoteItemId}-${index}`}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          );
        })}

        {/* Add Another Attribute Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button disabled={!allEntriesValid} variant='text' onClick={addEntry} sx={{ textTransform: 'none' }}>
            Add Another Attribute
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
